import userPreferences from "./userPreferences";
import { familyFor, resolveScope } from "./userPreferences.registry";

/**
 * A localStorage-shaped facade over the preferences client, for the key
 * families that used to live as one localStorage entry per key: a consumer
 * swaps `localStorage` for `preferenceStorage("<family>")` and keeps its
 * getItem / setItem / removeItem calls. `get` / `set` / `remove` are the
 * JSON-valued shortcuts (what getFromLocalStorage / saveOnLocalStorage did).
 *
 * One family = ONE server document `{ values: { [key]: value } }` at the
 * family's scope (SPEC-0061 §4.6). A portfolio family resolves the current
 * portfolio scope at call time; before a portfolio is known it reads the
 * fallback and drops writes (nothing to attach them to). A scope whose load
 * failed drops writes too: with the document unknown, a consumer's write is
 * the default it fell back to, which must not replace the saved value.
 */
const parseRaw = (raw) => {
  try {
    return { value: JSON.parse(raw), raw: false };
  } catch {
    return { value: String(raw), raw: true };
  }
};

const toRaw = (stored) =>
  stored && typeof stored === "object" && stored.__raw === true
    ? stored.value
    : JSON.stringify(stored);

const fromRaw = (raw) => {
  const { value, raw: isRaw } = parseRaw(raw);
  return isRaw ? { __raw: true, value } : value;
};

const unwrap = (stored) =>
  stored && typeof stored === "object" && stored.__raw === true ? stored.value : stored;

export const preferenceStorage = (familyName) => {
  const family = familyFor(familyName);
  const scope = () => {
    const current = userPreferences.currentPortfolioScope();
    return resolveScope(family, {
      portfolioId: current ? current.slice("portfolio:".length) : undefined,
    });
  };
  const values = () => {
    const s = scope();
    if (!s) return null;
    const doc = userPreferences.get(s, family.prefKey, null);
    return doc && typeof doc.values === "object" && doc.values ? doc.values : null;
  };
  const write = (mutate) => {
    const s = scope();
    if (!s || (userPreferences.isSettled(s) && !userPreferences.isLoaded(s))) return;
    const next = { ...(values() || {}) };
    mutate(next);
    userPreferences.set(s, family.prefKey, { values: next });
  };

  return {
    family,
    scope,
    getItem: (key) => {
      const current = values();
      return current && key in current ? toRaw(current[key]) : null;
    },
    setItem: (key, raw) => write((next) => (next[key] = fromRaw(raw))),
    removeItem: (key) => write((next) => delete next[key]),
    get: (key, fallback = null) => {
      const current = values();
      return current && key in current ? unwrap(current[key]) : fallback;
    },
    set: (key, value) => write((next) => (next[key] = value)),
    remove: (key) => write((next) => delete next[key]),
    /**
     * Keys minted per scenario (`<prefix><scenarioId>`) would grow the document
     * forever: a write re-inserts the key last and keeps the `max` most recent.
     */
    bounded: (prefix, max) => {
      const touch = (next, key, value) => {
        delete next[key];
        next[key] = value;
        const minted = Object.keys(next).filter((k) => k.startsWith(prefix));
        minted.slice(0, Math.max(0, minted.length - max)).forEach((k) => delete next[k]);
      };
      return {
        setItem: (key, raw) => write((next) => touch(next, key, fromRaw(raw))),
        set: (key, value) => write((next) => touch(next, key, value)),
      };
    },
  };
};
